import { isApiError } from "@colorglo/contracts/client";
import { LOCALES } from "@colorglo/i18n";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  Field,
  FieldDescription,
  FieldLabel,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsContent,
} from "@colorglo/ui/components";
import {
  ActionAuditLog,
  ActionPrevious,
  BlockerVacation,
  iconSize,
  NavOverview,
  ResourceTechnician,
  resourceIcons,
  SettingsHours,
} from "@colorglo/ui/icons";
import { Link, Navigate } from "@tanstack/react-router";
import { useId, useState } from "react";
import { toast } from "sonner";
import {
  type StaffMember,
  useResources,
  useSendPasswordReset,
  useServiceSettings,
  useSignOutEverywhere,
  useStaffMember,
  useUpdateResource,
  useUpdateStaffAccount,
} from "../../api/settings.ts";
import { useLocale, usePlural, useT } from "../../lib/locale.ts";
import { formatDayHeading, formatTime, zoned } from "../../lib/time.ts";
import { RemoveResourceSheet } from "./remove-resource-sheet.tsx";
import { SettingsCard, SettingsError, SettingsLoading, SettingsSection } from "./shell.tsx";
import { StaffActivity } from "./staff-activity.tsx";
import { StaffOverview } from "./staff-overview.tsx";
import { StaffSchedule } from "./staff-schedule.tsx";
import { StaffTimeOff } from "./staff-time-off.tsx";
import type { SettingsFranchise } from "./use-settings-franchise.ts";

export const STAFF_TABS = ["oversikt", "profil", "schema", "ledighet", "aktivitet"] as const;
export type StaffTab = (typeof STAFF_TABS)[number];

type Account = NonNullable<StaffMember["account"]>;

const TAB_ICONS = {
  oversikt: NavOverview,
  profil: ResourceTechnician,
  schema: SettingsHours,
  ledighet: BlockerVacation,
  aktivitet: ActionAuditLog,
} as const;

const TAB_LABELS = {
  oversikt: "settings.staff.tab.overview",
  profil: "settings.staff.tab.profile",
  schema: "settings.staff.tab.schedule",
  ledighet: "settings.staff.tab.time_off",
  aktivitet: "settings.staff.tab.activity",
} as const;

export function StaffDetail({
  franchise,
  resourceId,
  tab,
  onTabChange,
}: {
  readonly franchise: SettingsFranchise;
  readonly resourceId: string;
  readonly tab: StaffTab | undefined;
  readonly onTabChange: (tab: StaffTab) => void;
}) {
  const t = useT();
  const member = useStaffMember(franchise.id, resourceId);
  const resources = useResources(franchise.id);

  if (member.isError && isApiError(member.error) && member.error.error.code === "NOT_FOUND") {
    return <Navigate to="/installningar/personal" search={{ franchise: franchise.id }} />;
  }
  if (member.isPending || resources.isPending) return <SettingsLoading />;
  if (member.isError || resources.isError) {
    return <SettingsError onRetry={() => void member.refetch()} />;
  }

  const { resource, account } = member.data;
  const seesNumbers = franchise.permissions.includes("staff_report:read");
  const tabs = STAFF_TABS.filter(
    (entry) => (entry !== "profil" || account !== null) && (entry !== "oversikt" || seesNumbers),
  );
  const canManage = franchise.permissions.includes("resource:manage");
  const active = tab !== undefined && tabs.includes(tab) ? tab : (tabs[0] ?? "schema");
  const Icon = resourceIcons[resource.type as keyof typeof resourceIcons];

  return (
    <div className="flex flex-col gap-6">
      <Link
        to="/installningar/personal"
        search={{ franchise: franchise.id }}
        className="flex w-fit items-center gap-1 text-body-sm text-muted-foreground hover:text-foreground"
      >
        <ActionPrevious {...iconSize.inline} aria-hidden="true" />
        {t("settings.staff.back")}
      </Link>

      <header className="flex flex-wrap items-center gap-3">
        <span
          aria-hidden="true"
          className="size-4 shrink-0 rounded-full"
          style={{ backgroundColor: resource.color }}
        />
        {Icon !== undefined && <Icon {...iconSize.control} aria-hidden="true" />}
        <h2 className="text-h1">{account?.name ?? resource.name}</h2>
        {account !== null && (
          <Badge variant="secondary">
            {t(`settings.staff.role.${account.role}` as "settings.staff.role.franchise_staff")}
          </Badge>
        )}
        <Badge variant={resource.is_active ? "outline" : "secondary"}>
          {t(
            resource.is_active ? "settings.staff.status.active" : "settings.staff.status.inactive",
          )}
        </Badge>
        {account !== null && (
          <span className="w-full text-body-sm text-muted-foreground">{account.email}</span>
        )}
      </header>

      <Tabs value={active} onValueChange={(next) => onTabChange(next as StaffTab)}>
        <nav
          aria-label={account?.name ?? resource.name}
          className="-mx-1 flex gap-1 overflow-x-auto px-1 [scrollbar-width:none]"
        >
          {tabs.map((entry) => {
            const TabIcon = TAB_ICONS[entry];
            const current = entry === active;
            return (
              <button
                key={entry}
                type="button"
                aria-current={current ? "page" : undefined}
                onClick={() => onTabChange(entry)}
                className={`flex shrink-0 items-center gap-2 rounded-md px-3 py-2 text-body-sm whitespace-nowrap transition-colors hover:bg-accent hover:text-accent-foreground ${
                  current ? "bg-accent font-medium text-accent-foreground" : "text-muted-foreground"
                }`}
              >
                <TabIcon {...iconSize.inline} aria-hidden="true" />
                {t(TAB_LABELS[entry])}
              </button>
            );
          })}
        </nav>

        {seesNumbers && (
          <TabsContent value="oversikt" className="flex flex-col gap-8 pt-4">
            <StaffOverview
              franchiseId={franchise.id}
              resourceId={resourceId}
              name={account?.name ?? resource.name}
              timezone={franchise.timezone}
            />
          </TabsContent>
        )}

        {account !== null && (
          <TabsContent value="profil" className="flex flex-col gap-8 pt-4">
            <AccountSection
              key={`${account.email}-${account.name}`}
              franchiseId={franchise.id}
              resourceId={resourceId}
              account={account}
            />
            <SecuritySection
              franchiseId={franchise.id}
              resourceId={resourceId}
              account={account}
              timezone={franchise.timezone}
            />
            {canManage && <ActivationSection franchiseId={franchise.id} member={member.data} />}
          </TabsContent>
        )}

        <TabsContent value="schema" className="flex flex-col gap-8 pt-4">
          <StaffSchedule
            key={resource.id}
            franchiseId={franchise.id}
            resource={resource}
            palette={resources.data.palette}
            editable={canManage}
          />
          {account === null && canManage && (
            <ActivationSection franchiseId={franchise.id} member={member.data} />
          )}
        </TabsContent>

        <TabsContent value="ledighet" className="flex flex-col gap-8 pt-4">
          <StaffTimeOff
            franchiseId={franchise.id}
            resourceId={resourceId}
            timezone={franchise.timezone}
            editable={
              franchise.permissions.includes("blocker:manage") && (account?.editable ?? true)
            }
          />
        </TabsContent>

        <TabsContent value="aktivitet" className="flex flex-col gap-8 pt-4">
          <StaffActivity
            franchiseId={franchise.id}
            resourceId={resourceId}
            name={account?.name ?? resource.name}
            timezone={franchise.timezone}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function AccountSection({
  franchiseId,
  resourceId,
  account,
}: {
  readonly franchiseId: string;
  readonly resourceId: string;
  readonly account: Account;
}) {
  const t = useT();
  const id = useId();
  const update = useUpdateStaffAccount(franchiseId, resourceId);

  const [name, setName] = useState(account.name);
  const [email, setEmail] = useState(account.email);
  const [phone, setPhone] = useState(account.phone ?? "");
  const [locale, setLocale] = useState(account.locale);

  const locked = !account.editable;
  const dirty =
    name !== account.name ||
    email !== account.email ||
    phone !== (account.phone ?? "") ||
    locale !== account.locale;

  const onSave = () => {
    update.mutate(
      {
        name,
        ...(email === account.email ? {} : { email }),
        phone: phone === "" ? null : phone,
        locale,
      },
      {
        onSuccess: () => toast.success(t("settings.staff.account.saved")),
        onError: (error) => {
          const code = isApiError(error) ? error.error.code : null;
          toast.error(
            code === "EMAIL_TAKEN"
              ? t("error.email_taken")
              : code === "SHARED_ACCOUNT"
                ? t("error.shared_account")
                : t("settings.error"),
          );
        },
      },
    );
  };

  return (
    <SettingsSection
      heading={t("settings.staff.account.heading")}
      body={t("settings.staff.account.body")}
    >
      <SettingsCard>
        {locked && (
          <p className="text-body-sm text-muted-foreground">
            {t("settings.staff.account.owner_locked")}
          </p>
        )}
        {account.shared && !locked && (
          <p className="text-body-sm text-muted-foreground">
            {t("settings.staff.account.shared", { name: account.name })}
          </p>
        )}

        <div className="grid max-w-2xl gap-5 sm:grid-cols-2">
          <Field>
            <FieldLabel htmlFor={`${id}-name`}>{t("settings.staff.account.name")}</FieldLabel>
            <Input
              id={`${id}-name`}
              autoComplete="off"
              disabled={locked}
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </Field>

          <Field>
            <FieldLabel htmlFor={`${id}-email`}>{t("settings.staff.account.email")}</FieldLabel>
            <Input
              id={`${id}-email`}
              type="email"
              autoComplete="off"
              disabled={locked || account.shared}
              aria-describedby={`${id}-email-hint`}
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
            <FieldDescription id={`${id}-email-hint`}>
              {t("settings.staff.account.email_hint")}
            </FieldDescription>
          </Field>

          <Field>
            <FieldLabel htmlFor={`${id}-phone`}>{t("settings.staff.account.phone")}</FieldLabel>
            <Input
              id={`${id}-phone`}
              type="tel"
              autoComplete="off"
              disabled={locked}
              value={phone}
              onChange={(event) => setPhone(event.target.value)}
            />
          </Field>

          <Field>
            <FieldLabel htmlFor={`${id}-locale`}>{t("settings.staff.account.language")}</FieldLabel>
            <Select value={locale} onValueChange={setLocale} disabled={locked}>
              <SelectTrigger id={`${id}-locale`} className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LOCALES.map((entry) => (
                  <SelectItem key={entry} value={entry}>
                    {t(`language.${entry}` as "language.sv")}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        </div>

        {!locked && (
          <div className="flex gap-3">
            <Button onClick={onSave} disabled={!dirty || name === "" || update.isPending}>
              {t("settings.save")}
            </Button>
            {dirty && (
              <Button
                variant="ghost"
                onClick={() => {
                  setName(account.name);
                  setEmail(account.email);
                  setPhone(account.phone ?? "");
                  setLocale(account.locale);
                }}
              >
                {t("settings.cancel")}
              </Button>
            )}
          </div>
        )}
      </SettingsCard>
    </SettingsSection>
  );
}

function SecuritySection({
  franchiseId,
  resourceId,
  account,
  timezone,
}: {
  readonly franchiseId: string;
  readonly resourceId: string;
  readonly account: Account;
  readonly timezone: string;
}) {
  const t = useT();
  const plural = usePlural();
  const locale = useLocale();
  const reset = useSendPasswordReset(franchiseId, resourceId);
  const signOut = useSignOutEverywhere(franchiseId, resourceId);
  const [confirm, setConfirm] = useState<"reset" | "sign_out" | null>(null);

  const lastActive =
    account.last_active_at === null
      ? t("settings.staff.security.never")
      : (() => {
          const when = zoned(account.last_active_at, timezone);
          return t("settings.staff.security.last_active", {
            when: `${formatDayHeading(when.toPlainDate(), locale)} ${formatTime(when)}`,
          });
        })();

  const onConfirm = () => {
    if (confirm === "reset") {
      reset.mutate(undefined, {
        onSuccess: () =>
          toast.success(t("settings.staff.security.reset_sent", { email: account.email })),
        onError: () => toast.error(t("settings.error")),
      });
    }
    if (confirm === "sign_out") {
      signOut.mutate(undefined, {
        onSuccess: () =>
          toast.success(t("settings.staff.security.signed_out", { name: account.name })),
        onError: () => toast.error(t("settings.error")),
      });
    }
    setConfirm(null);
  };

  return (
    <SettingsSection heading={t("settings.staff.security.heading")}>
      <SettingsCard>
        <div className="flex flex-col gap-1">
          <p className="text-body">{lastActive}</p>
          {account.last_active_at !== null && (
            <p className="text-body-sm text-muted-foreground">
              {plural("settings.staff.security.sessions", account.active_sessions)}
            </p>
          )}
        </div>

        {account.editable && (
          <div className="flex flex-wrap gap-3">
            <Button
              variant="outline"
              disabled={account.status !== "active" || reset.isPending}
              onClick={() => setConfirm("reset")}
            >
              {t("settings.staff.security.reset")}
            </Button>
            <Button
              variant="outline"
              disabled={account.shared || account.active_sessions === 0 || signOut.isPending}
              onClick={() => setConfirm("sign_out")}
            >
              {t("settings.staff.security.sign_out")}
            </Button>
          </div>
        )}
      </SettingsCard>

      <AlertDialog open={confirm !== null} onOpenChange={(open) => open || setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirm === "reset"
                ? t("settings.staff.security.reset_title", { email: account.email })
                : t("settings.staff.security.sign_out_title", { name: account.name })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirm === "reset"
                ? t("settings.staff.security.reset_body")
                : t("settings.staff.security.sign_out_body", { name: account.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("settings.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={onConfirm}>
              {confirm === "reset"
                ? t("settings.staff.security.reset_send")
                : t("settings.staff.security.sign_out_confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SettingsSection>
  );
}

function ActivationSection({
  franchiseId,
  member,
}: {
  readonly franchiseId: string;
  readonly member: StaffMember;
}) {
  const t = useT();
  const services = useServiceSettings(franchiseId);
  const update = useUpdateResource(franchiseId);
  const [removing, setRemoving] = useState(false);
  const { resource, account } = member;
  const name = account?.name ?? resource.name;

  if (account !== null && !account.editable) return null;

  return (
    <SettingsSection heading={t("settings.staff.danger.heading")}>
      <SettingsCard>
        {resource.is_active ? (
          <>
            <p className="max-w-prose text-body-sm text-muted-foreground">
              {t("settings.staff.danger.body", { name })}
            </p>
            <div>
              <Button variant="destructive" onClick={() => setRemoving(true)}>
                {t("settings.staff.danger.deactivate", { name })}
              </Button>
            </div>
          </>
        ) : (
          <>
            <p className="max-w-prose text-body-sm text-muted-foreground">
              {t("settings.staff.danger.reactivate_body", { name })}
            </p>
            <div>
              <Button
                variant="outline"
                disabled={update.isPending}
                onClick={() =>
                  update.mutate(
                    { resourceId: resource.id, is_active: true },
                    { onError: () => toast.error(t("settings.error")) },
                  )
                }
              >
                {t("settings.staff.reactivate")}
              </Button>
            </div>
          </>
        )}
      </SettingsCard>

      {removing && (
        <RemoveResourceSheet
          franchiseId={franchiseId}
          resource={resource}
          services={services.data?.data ?? []}
          onClose={() => setRemoving(false)}
        />
      )}
    </SettingsSection>
  );
}
